/**
 * Configurações → Mensagens prontas.
 *
 * ── O que se escreve aqui ────────────────────────────────────────────────
 *
 * O texto que a equipe repete o dia inteiro: a saudação, o endereço, o
 * horário, a explicação da troca. Ele aparece no atendimento dentro da caixa
 * de mensagens prontas, na aba "Da organização", e entra no campo de texto
 * para ser editado antes de enviar.
 *
 * ── A forma é a das outras seções, e isso não é detalhe ─────────────────
 *
 * `admin__secao`, `admin__nota` e o formulário dentro de um `Cartao` folgado:
 * é o mesmo esqueleto de Etiquetas e de Etapas do funil. A primeira versão
 * desta tela inventou classes próprias e ficou com a cara errada — o campo de
 * texto sem largura, o formulário solto no meio do nada, e um aviso amarelo do
 * tamanho da tela para dizer algo que é explicação, não alarme.
 *
 * ── A lista vem ANTES do formulário ─────────────────────────────────────
 *
 * Quem abre esta tela quase sempre vem corrigir uma mensagem que já existe, e
 * não escrever a primeira. É a ordem que Etiquetas usa, pelo mesmo motivo.
 *
 * ── Uma lista com edição no lugar, e não um diálogo por item ────────────
 *
 * São textos curtos que se corrigem com frequência — uma vírgula, um telefone
 * que mudou. Abrir diálogo para cada correção transformaria dois segundos de
 * trabalho em quatro cliques.
 *
 * ── Quem não pode escrever vê a lista, e não o formulário ───────────────
 *
 * Escrever pede `mensagem_pronta × editar` (ADR-0034, #1192). Antes, o
 * formulário e os botões apareciam para todo mundo, e quem atende preenchia,
 * clicava e recebia "Não foi possível criar", sem motivo: a API não diz o
 * motivo de uma recusa de permissão. Esconder é o que evita a porta que só
 * existe para dizer não.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MensagemPronta } from '@cliqcrm/contracts';
import { useApi, useMensagensProntas } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { usePapel } from '@/shared/sessao/contexto';
import { Botao, Cartao } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { EDITA_MENSAGENS_PRONTAS, LE_CONVERSA } from '@/entities/acesso/papel';
import {
  AvisoDeContato,
  CamposDaMensagemPronta,
  rascunhoCompleto,
  temContatoNovo,
  type RascunhoDeMensagemPronta,
} from '@/entities/atendimento/mensagem-pronta';
import { NovaMensagemPronta } from '@/features/atendimento/criar-mensagem-pronta';

export function SecaoMensagensProntas(): ReactNode {
  const cache = useQueryClient();
  const { data, isPending, isError } = useMensagensProntas();
  const papel = usePapel();
  const podeEditar = papel != null && EDITA_MENSAGENS_PRONTAS.has(papel);
  // A lista é de quem lê conversa. O Suporte Cliqfy não a lê, e "você usa
  // estas mensagens no atendimento" seria falso para ele.
  const usaNoAtendimento = papel != null && LE_CONVERSA.has(papel);

  const invalidar = (): void => {
    void cache.invalidateQueries({ queryKey: chaves.mensagensProntas() });
  };

  const itens = data?.itens ?? [];

  return (
    <section className="admin__secao" aria-label="Mensagens prontas">
      {/*
        A regra das 24 h é EXPLICAÇÃO, e por isso `admin__nota` e não faixa de
        atenção. Quem escreve a lista precisa saber que ela não serve para
        reabrir conversa parada — senão cadastra uma "retomada de contato" que o
        WhatsApp nunca entrega e conclui que o CliqCRM está com defeito. Mas
        isso é instrução, e o amarelo gasta a atenção que o alarme de verdade
        vai precisar.

        O texto também não repete o subtítulo da seção, que já diz o que estas
        mensagens são. Dizer duas vezes a mesma coisa em duas linhas seguidas
        ensina a pular a segunda.
      */}
      <p className="admin__nota">
        No atendimento, estes textos aparecem na caixa de mensagens prontas e entram no campo para
        serem ajustados antes de enviar. Valem <strong>dentro das 24 h</strong> desde a última
        mensagem do cliente; para falar com quem sumiu antes disso, o caminho é o modelo aprovado
        pela Meta, na outra aba da mesma caixa.
      </p>

      {!podeEditar && usaNoAtendimento && (
        <p className="admin__nota">
          Você usa estas mensagens no atendimento. Para criar ou corrigir uma, fale com o
          administrador da organização.
        </p>
      )}

      {isPending && <p className="admin__nota">Buscando as mensagens da organização…</p>}
      {isError && <Faixa tom="bloqueado">Não foi possível buscar as mensagens prontas.</Faixa>}

      {data &&
        itens.length === 0 &&
        (podeEditar ? (
          <p className="admin__nota">
            Nenhuma mensagem pronta ainda. A primeira costuma ser a saudação — é a que a equipe mais
            redigita.
          </p>
        ) : (
          <p className="admin__nota">Nenhuma mensagem pronta ainda.</p>
        ))}

      {itens.length > 0 && (
        <ul className="admin__lista prontas__lista">
          {itens.map((mensagem) => (
            <LinhaDaMensagem
              key={mensagem.id}
              mensagem={mensagem}
              podeEditar={podeEditar}
              aoMudar={invalidar}
            />
          ))}
        </ul>
      )}

      {podeEditar && <NovaMensagemPronta parte="admin__form" />}
    </section>
  );
}

/**
 * Uma mensagem da lista, com edição no lugar.
 *
 * O estado de edição é local à linha: duas pessoas da mesma equipe mexendo em
 * mensagens diferentes é caso comum, e um estado no topo faria abrir uma fechar
 * a outra.
 */
function LinhaDaMensagem({
  mensagem,
  podeEditar,
  aoMudar,
}: {
  mensagem: MensagemPronta;
  /** Sem a permissão, a linha é só leitura: sem "Editar" nem "Remover". */
  podeEditar: boolean;
  aoMudar: () => void;
}): ReactNode {
  const api = useApi();
  const [editando, definirEditando] = useState(false);
  const gravado: RascunhoDeMensagemPronta = { titulo: mensagem.titulo, texto: mensagem.texto };
  const [rascunho, definirRascunhoGuardado] = useState<RascunhoDeMensagemPronta>(gravado);
  const [confirmando, definirConfirmando] = useState(false);
  /*
   * Telefone ou e-mail NOVO pede confirmação antes de salvar (decisão de
   * 03/11/2026). O que já estava gravado não pede de novo.
   */
  const [contatoConfirmado, definirContatoConfirmado] = useState(false);
  const definirRascunho = (novo: RascunhoDeMensagemPronta): void => {
    definirContatoConfirmado(false);
    definirRascunhoGuardado(novo);
  };
  const pedeConfirmacao = temContatoNovo(rascunho, gravado) && !contatoConfirmado;

  const salvar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.editarMensagemPronta({
        caminho: { id: mensagem.id },
        corpo: { titulo: rascunho.titulo.trim(), texto: rascunho.texto.trim() },
      });
    },
    onSuccess: () => {
      definirEditando(false);
      aoMudar();
    },
  });

  const remover = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.removerMensagemPronta({ caminho: { id: mensagem.id } });
    },
    onSuccess: aoMudar,
  });

  /* Uma faixa só: as duas ações não acontecem ao mesmo tempo, e duas caixas de
     erro empilhadas na mesma linha seria ruído. */
  const falha = salvar.isError
    ? recadoDeErro(salvar.error, { generica: 'Não foi possível salvar a correção.' })
    : remover.isError
      ? recadoDeErro(remover.error, { generica: 'Não foi possível remover.' })
      : null;

  return (
    <li>
      <Cartao variante="folgado" parte="prontas__item">
        {editando ? (
          <>
            <CamposDaMensagemPronta rascunho={rascunho} aoMudar={definirRascunho} focar />
            {pedeConfirmacao && (
              <AvisoDeContato aoConfirmar={() => definirContatoConfirmado(true)} />
            )}
            {falha && <Faixa tom="bloqueado">{falha}</Faixa>}
            <div className="prontas__acoes">
              <Botao
                variante="primario"
                onClick={() => salvar.mutate()}
                desabilitado={!rascunhoCompleto(rascunho) || salvar.isPending || pedeConfirmacao}
                motivo={
                  salvar.isPending
                    ? 'Salvando…'
                    : pedeConfirmacao
                      ? 'Confirme o telefone ou e-mail acima.'
                      : 'Título e texto não podem ficar vazios.'
                }
              >
                {salvar.isPending ? 'Salvando…' : 'Salvar'}
              </Botao>
              <Botao
                onClick={() => {
                  // Volta ao que está gravado: sair da edição sem desfazer
                  // deixaria o texto alterado na tela sem estar salvo em lugar
                  // nenhum. E o erro da tentativa que não salvou sai junto,
                  // senão ficaria na linha, sob o texto que não mudou.
                  definirRascunho(gravado);
                  salvar.reset();
                  definirEditando(false);
                }}
              >
                Cancelar
              </Botao>
            </div>
          </>
        ) : (
          <>
            <div className="prontas__cabecalho">
              <strong className="prontas__titulo">{mensagem.titulo}</strong>
              {podeEditar && (
                <div className="prontas__acoes">
                  <Botao
                    miudo
                    onClick={() => {
                      // Parte do que está gravado agora: a lista pode ter
                      // sido corrigida por outra pessoa desde que a linha nasceu.
                      // E sem o erro de uma tentativa anterior.
                      definirRascunho(gravado);
                      salvar.reset();
                      remover.reset();
                      definirEditando(true);
                    }}
                  >
                    Editar
                  </Botao>
                  {/*
                  A remoção pede confirmação NA PRÓPRIA linha.

                  Não é diálogo porque o risco é pequeno — o texto se reescreve
                  —, e não é clique único porque a lista é de toda a equipe:
                  quem apaga não é quem vai sentir falta.
                */}
                  {confirmando ? (
                    <>
                      <Botao
                        miudo
                        variante="perigo"
                        onClick={() => remover.mutate()}
                        desabilitado={remover.isPending}
                        motivo="Removendo…"
                      >
                        Confirmar
                      </Botao>
                      <Botao miudo onClick={() => definirConfirmando(false)}>
                        Não
                      </Botao>
                    </>
                  ) : (
                    <Botao miudo onClick={() => definirConfirmando(true)}>
                      Remover
                    </Botao>
                  )}
                </div>
              )}
            </div>
            <p className="prontas__texto">{mensagem.texto}</p>
            {falha && <Faixa tom="bloqueado">{falha}</Faixa>}
          </>
        )}
      </Cartao>
    </li>
  );
}
